import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Mail, User, Gift, CheckCircle, Sparkles, AlertCircle } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const SCRIPT_URL = process.env.REACT_APP_GOOGLE_SCRIPT_URL || '';

interface FormData {
  name: string;
  email: string;
  interests: string[];
  referralSource: string;
}

const Waitlist: React.FC = () => {
  const { t } = useLanguage();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    interests: [],
    referralSource: '',
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const interestOptions = [
    { id: 'diffuser', label: t('waitlist.form.interestOptions.diffuser') },
    { id: 'spray', label: t('waitlist.form.interestOptions.spray') },
    { id: 'candle', label: t('waitlist.form.interestOptions.candle') },
    { id: 'bundle', label: t('waitlist.form.interestOptions.bundle') },
    { id: 'subscription', label: t('waitlist.form.interestOptions.subscription') },
    { id: 'all', label: t('waitlist.form.interestOptions.all') },
  ];

  const referralOptions = [
    { id: 'social', label: t('waitlist.form.referralOptions.social') },
    { id: 'friend', label: t('waitlist.form.referralOptions.friend') },
    { id: 'search', label: t('waitlist.form.referralOptions.search') },
    { id: 'market', label: t('waitlist.form.referralOptions.market') },
    { id: 'other', label: t('waitlist.form.referralOptions.other') },
  ];

  const benefits = [
    { icon: Gift, title: t('waitlist.benefits.discount.title'), description: t('waitlist.benefits.discount.description') },
    { icon: Sparkles, title: t('waitlist.benefits.earlyAccess.title'), description: t('waitlist.benefits.earlyAccess.description') },
    { icon: CheckCircle, title: t('waitlist.benefits.updates.title'), description: t('waitlist.benefits.updates.description') },
  ];

  const toggleInterest = (id: string) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.includes(id)
        ? prev.interests.filter(i => i !== id)
        : [...prev.interests, id],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);

    if (!SCRIPT_URL) {
      setErrorMessage('Signup is not configured yet. Please email us at help@nestbloom.com');
      setIsSubmitting(false);
      return;
    }

    try {
      const body = new URLSearchParams({
        name: formData.name,
        email: formData.email,
        interests: formData.interests.join(', ') || 'Not specified',
        referralSource: formData.referralSource || 'Not specified',
        submittedAt: new Date().toLocaleString(),
      });

      // Google Apps Script requires no-cors; response will be opaque but data is written.
      await fetch(SCRIPT_URL, { method: 'POST', mode: 'no-cors', body });

      setIsSubmitted(true);
    } catch (error) {
      setErrorMessage('Something went wrong. Please email us at help@nestbloom.com');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <section id="waitlist" className="py-24 bg-mist">
        <div className="container mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="text-center max-w-xl mx-auto"
          >
            <div className="w-20 h-20 bg-herb-green rounded-full flex items-center justify-center mx-auto mb-8">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h2 className="text-4xl font-elegant font-bold text-forest mb-3">
              {t('waitlist.form.success.title')}
            </h2>
            <p className="text-xl font-italic italic text-herb-green mb-6">
              {t('waitlist.form.success.subtitle')}
            </p>
            <div className="bg-white rounded-2xl p-8 border border-herb-light/30">
              <p className="text-charcoal/80 leading-relaxed mb-6">
                {t('waitlist.form.success.message')}
              </p>
              <div className="grid grid-cols-3 gap-4">
                {benefits.map((b, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, y: 15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.15 }}
                    className="text-center"
                  >
                    <div className="w-10 h-10 bg-herb-green/10 rounded-full flex items-center justify-center mx-auto mb-2">
                      <b.icon className="w-5 h-5 text-herb-green" />
                    </div>
                    <p className="text-xs font-semibold text-forest">{b.title}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section id="waitlist" className="py-24 bg-mist">
      <div className="container mx-auto px-6">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 40 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-14 max-w-2xl mx-auto"
        >
          <span className="inline-block text-herb-green text-sm font-semibold tracking-widest uppercase mb-4">
            Early Access
          </span>
          <h2 className="text-4xl md:text-5xl font-elegant font-bold text-forest mb-4">
            {t('waitlist.title')}
          </h2>
          <p className="text-lg text-warm-gray mb-6">
            {t('waitlist.subtitle')}
          </p>

          {/* Discount callout */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="inline-flex items-center gap-3 px-6 py-3 bg-white rounded-full border border-herb-green/25 shadow-sm"
          >
            <Gift className="w-5 h-5 text-herb-green" />
            <span className="text-2xl font-bold text-herb-green">15% OFF</span>
            <span className="text-charcoal/70 text-sm">your first order — first 50 signups only</span>
          </motion.div>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start max-w-5xl mx-auto">
          {/* Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h3 className="text-xl font-bold text-forest mb-6">
              {t('waitlist.memberBenefits')}
            </h3>
            <div className="space-y-5">
              {benefits.map((b, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 15 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.3 + i * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-11 h-11 bg-herb-green rounded-xl flex items-center justify-center flex-shrink-0">
                    <b.icon className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="font-semibold text-forest mb-0.5">{b.title}</p>
                    <p className="text-sm text-warm-gray">{b.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* Social proof placeholder */}
            <div className="mt-10 p-5 bg-white/70 rounded-2xl border border-herb-light/25">
              <p className="text-sm text-warm-gray italic">
                "I built NestBloom because I needed it. City living is beautiful and exhausting — I wanted something that made my apartment feel like a place to actually rest."
              </p>
              <p className="text-xs text-herb-green font-semibold mt-2">— Founder, NestBloom</p>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={inView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="bg-white rounded-2xl p-8 border border-herb-light/20 shadow-sm"
          >
            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Name */}
              <div>
                <label className="block text-sm font-semibold text-forest mb-1.5">
                  {t('waitlist.form.name')}
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray/50" />
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={e => setFormData(p => ({ ...p, name: e.target.value }))}
                    required
                    placeholder={t('waitlist.form.placeholders.name')}
                    className="w-full pl-9 pr-4 py-3 border border-linen rounded-xl focus:outline-none focus:ring-2 focus:ring-herb-green/30 focus:border-herb-green/50 transition-all text-sm"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-sm font-semibold text-forest mb-1.5">
                  {t('waitlist.form.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-gray/50" />
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                    required
                    placeholder={t('waitlist.form.placeholders.email')}
                    className="w-full pl-9 pr-4 py-3 border border-linen rounded-xl focus:outline-none focus:ring-2 focus:ring-herb-green/30 focus:border-herb-green/50 transition-all text-sm"
                  />
                </div>
              </div>

              {/* Interests */}
              <div>
                <label className="block text-sm font-semibold text-forest mb-2">
                  {t('waitlist.form.interests')}
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {interestOptions.map(opt => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => toggleInterest(opt.id)}
                      className={`py-2 px-3 rounded-lg border text-left text-xs font-medium transition-all duration-200 ${
                        formData.interests.includes(opt.id)
                          ? 'border-herb-green bg-herb-green text-white'
                          : 'border-linen text-warm-gray hover:border-herb-sage hover:text-forest'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Referral */}
              <div>
                <label className="block text-sm font-semibold text-forest mb-1.5">
                  {t('waitlist.form.referralSource')}
                </label>
                <select
                  value={formData.referralSource}
                  onChange={e => setFormData(p => ({ ...p, referralSource: e.target.value }))}
                  className="w-full px-4 py-3 border border-linen rounded-xl focus:outline-none focus:ring-2 focus:ring-herb-green/30 focus:border-herb-green/50 transition-all text-sm text-charcoal bg-white"
                >
                  <option value="">{t('waitlist.form.placeholders.referralSource')}</option>
                  {referralOptions.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>

              {/* Error */}
              {errorMessage && (
                <motion.div
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="p-3.5 bg-red-50 border border-red-200 rounded-xl flex gap-3 items-start"
                >
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{errorMessage}</p>
                </motion.div>
              )}

              {/* Submit */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: isSubmitting ? 1 : 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full py-3.5 rounded-xl font-semibold text-white transition-all duration-300 ${
                  isSubmitting ? 'bg-herb-green/50 cursor-not-allowed' : 'bg-herb-green hover:bg-forest shadow-sm'
                }`}
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Joining…
                  </span>
                ) : (
                  t('waitlist.form.submit')
                )}
              </motion.button>
            </form>

            <p className="text-xs text-warm-gray/60 mt-4 text-center">
              {t('waitlist.privacy')}
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Waitlist;
